import { AnimatePresence, motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Minus, Plus, ShoppingBag, Trash2, Truck, X } from "lucide-react";
import { useCart } from "../context/CartContext";
import { money } from "./bits";

export default function CartDrawer() {
  const { items, open, setOpen, setQty, removeItem, subtotal } = useCart();
  const navigate = useNavigate();

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setOpen(false)}
            className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm"
          />
          <motion.aside
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "spring", stiffness: 300, damping: 32 }}
            data-testid="cart-drawer"
            className="fixed right-0 top-0 z-[70] flex h-full w-full max-w-md flex-col border-l border-[#39FF14]/20 bg-[#071009]"
          >
            <div className="flex items-center justify-between border-b border-[#39FF14]/10 px-6 py-5">
              <div className="flex items-center gap-2">
                <ShoppingBag className="h-5 w-5 text-[#39FF14]" />
                <h2 className="font-display text-lg font-bold">Your Cart</h2>
              </div>
              <button data-testid="cart-close-button" onClick={() => setOpen(false)} className="rounded-full border border-[#39FF14]/20 p-2 text-[#86EFAC] hover:border-[#39FF14]/50">
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-4">
              {items.length === 0 ? (
                <div className="flex h-full flex-col items-center justify-center gap-3 text-center">
                  <img src="/img/brand/alien.png" alt="SuperTechLED alien mascot" className="h-32 w-auto opacity-95 drop-shadow-[0_0_30px_rgba(57,255,20,0.3)]" />
                  <p className="font-display text-lg font-semibold text-[#F0FDF4]">Nothing growing here yet</p>
                  <p className="text-sm text-[#9FB8A8]">Add a light and get your garden glowing.</p>
                  <button
                    data-testid="cart-empty-shop-button"
                    onClick={() => { setOpen(false); navigate("/shop"); }}
                    className="mt-2 rounded-full bg-[#39FF14] px-6 py-2.5 font-display text-sm font-bold text-[#050B07]"
                  >
                    Browse the range
                  </button>
                </div>
              ) : (
                <ul className="space-y-4">
                  {items.map((i) => (
                    <motion.li
                      layout
                      key={i.key}
                      data-testid={`cart-item-${i.slug}-${i.sku}`}
                      className="flex gap-4 rounded-xl border border-[#39FF14]/10 bg-[#0A150F] p-3"
                    >
                      <div className="h-20 w-20 shrink-0 overflow-hidden rounded-lg bg-[#050B07]">
                        <img src={i.image} alt={i.name} className="h-full w-full object-contain p-1.5" />
                      </div>
                      <div className="flex flex-1 flex-col">
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <div className="font-display text-sm font-bold text-[#F0FDF4]">{i.name}</div>
                            <div className="font-mono2 text-[10px] uppercase tracking-widest text-[#4ADE80]">{i.variantName}</div>
                          </div>
                          <button data-testid={`cart-remove-${i.slug}-${i.sku}`} onClick={() => removeItem(i.key)} className="text-[#9FB8A8] hover:text-red-400">
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                        <div className="mt-auto flex items-center justify-between pt-2">
                          <div className="flex items-center gap-2 rounded-full border border-[#39FF14]/20 px-2 py-1">
                            <button data-testid={`cart-qty-minus-${i.slug}-${i.sku}`} onClick={() => setQty(i.key, i.qty - 1)} className="text-[#86EFAC] hover:text-[#39FF14]">
                              <Minus className="h-3.5 w-3.5" />
                            </button>
                            <span className="w-5 text-center font-mono2 text-xs">{i.qty}</span>
                            <button data-testid={`cart-qty-plus-${i.slug}-${i.sku}`} onClick={() => setQty(i.key, i.qty + 1)} className="text-[#86EFAC] hover:text-[#39FF14]">
                              <Plus className="h-3.5 w-3.5" />
                            </button>
                          </div>
                          <div className="font-display text-sm font-bold text-[#F0FDF4]">{money(i.price * i.qty)}</div>
                        </div>
                      </div>
                    </motion.li>
                  ))}
                </ul>
              )}
            </div>

            {items.length > 0 && (
              <div className="border-t border-[#39FF14]/10 px-6 py-5">
                <div className="mb-2 flex items-center gap-2 rounded-lg border border-[#39FF14]/20 bg-[#0E1C13] px-3 py-2">
                  <Truck className="h-4 w-4 text-[#39FF14]" />
                  <span className="text-xs text-[#86EFAC]">Free express shipping Australia-wide</span>
                </div>
                <div className="flex items-center justify-between py-2">
                  <span className="font-mono2 text-xs uppercase tracking-widest text-[#9FB8A8]">Subtotal</span>
                  <span data-testid="cart-subtotal" className="font-display text-xl font-bold text-[#F0FDF4]">{money(subtotal)}</span>
                </div>
                <button
                  data-testid="cart-checkout-button"
                  onClick={() => { setOpen(false); navigate("/checkout"); }}
                  className="group flex w-full items-center justify-center gap-2 rounded-full bg-[#39FF14] py-3.5 font-display text-sm font-bold text-[#050B07] transition-shadow hover:shadow-[0_0_30px_rgba(57,255,20,0.4)]"
                >
                  Checkout
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </button>
              </div>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
